import {ProjectFeatureFlag, SHOW_LAST_X_DAYS_UNIT, SHOW_LAST_X_DAYS_VALUES} from 'common/report/constants';
import {
  AnalysisType,
  ChartPlotStyle,
  ChartType,
  ConversionWindowUnit,
  FunnelOrder,
  FunnelReentryModeType,
  FUNNELS_MATH_TYPES,
  InsightsBookmarkParams,
  InsightsResourceType,
  MetricType,
  PRIMITIVE_MATH_TYPES,
  RETENTION_MATH_TYPES,
  RetentionAlignmentType,
  ValueRepresentationType,
} from 'common/types/reports/bookmark';
import {TimeUnit} from 'common/widgets/date-range-picker/models/types';

import {ALL_EVENTS_MAX_DAY_DURATION, TIME_UNITS} from '../models/constants';
import {DATE_RANGE_TYPE, RANGE_TO_TIME_CLAUSE_PARAMS, RANGES} from '../models/time-clause';
import {DEFAULT_STEPS_TABLE_COLUMNS} from '../visualization/constants';
import {_getDefaultInsightsSortConfig} from './index';
import {FILTER_DETERMINER_ALL, INSIGHTS_METRICS_LIMIT_OVERRIDES, QueryMode} from './constants';
import type {BehaviorShowClause} from './types';

/**
 * Creates an empty Insights bookmark with default values.
 */
export function createDefaultInsightsBookmark({
  dataGroupId,
  dataset,
  queryMode = QueryMode.Events,
  showLastXDays = null,
  defaultToConversionSession,
  defaultToMathSession,
  metricLevelDataGroups = false,
}: {
  dataGroupId?: string;
  dataset?: string;
  queryMode?: QueryMode;
  showLastXDays?: ProjectFeatureFlag | null;
  defaultToConversionSession?: boolean;
  defaultToMathSession?: boolean;
  metricLevelDataGroups?: boolean;
} = {}): InsightsBookmarkParams {
  const showMapping: Record<QueryMode, BehaviorShowClause | null> = {
    [QueryMode.Groups]: {
      behavior: {
        type: MetricType.People,
        dataset,
        resourceType: InsightsResourceType.People,
        name: `$all_people`,
        dataGroupId,
      },
      display: {},
      measurement: {
        math: PRIMITIVE_MATH_TYPES.TOTAL,
      },
    },
    [QueryMode.Events]: null,
    [QueryMode.Funnels]: {
      behavior: {
        type: MetricType.Funnel,
        dataset,
        resourceType: InsightsResourceType.Events,
        behaviors: [
          {
            type: MetricType.Event,
            name: null,
            filtersDeterminer: FILTER_DETERMINER_ALL,
            filters: [],
          },
          {
            type: MetricType.Event,
            name: null,
            filtersDeterminer: FILTER_DETERMINER_ALL,
            filters: [],
          },
        ],
        name: null,
        dataGroupId,
        funnelOrder: FunnelOrder.Loose,
        aggregateBy: [],
        conversionWindowDuration: defaultToConversionSession ? 1 : 7,
        conversionWindowUnit: defaultToConversionSession ? ConversionWindowUnit.Session : ConversionWindowUnit.Day,
        funnelReentryMode: FunnelReentryModeType.Basic,
      },
      display: {},
      measurement: {
        math: defaultToMathSession ? FUNNELS_MATH_TYPES.SESSION : FUNNELS_MATH_TYPES.CONVERSION_RATE_UNIQUE,
        stepIndex: null,
        actionMode: null,
        actionStep: null,
      },
      isExpanded: true,
    },
    [QueryMode.Retention]: {
      behavior: {
        type: MetricType.Retention,
        dataset,
        resourceType: InsightsResourceType.Events,
        behaviors: [
          {
            type: MetricType.Event,
            name: null,
            filtersDeterminer: FILTER_DETERMINER_ALL,
            filters: [],
          },
          {
            type: MetricType.Event,
            name: null,
            filtersDeterminer: FILTER_DETERMINER_ALL,
            filters: [],
          },
        ],
        name: null,
        dataGroupId,
        retentionType: `birth`,
        retentionAlignmentType: RetentionAlignmentType.Birth,
        retentionUnit: TimeUnit.Day,
        retentionUnboundedMode: `none`,
      },
      display: {},
      measurement: {
        math: RETENTION_MATH_TYPES.RETENTION_RATE,
        retentionBucketIndex: 0,
      },
      isExpanded: true,
    },
  };
  const chartType =
    queryMode === QueryMode.Groups
      ? ChartType.Bar
      : queryMode === QueryMode.Funnels
        ? ChartType.FunnelSteps
        : queryMode === QueryMode.Retention
          ? ChartType.RetentionCurve
          : ChartType.Line;

  const showEntry = showMapping[queryMode];
  const showLastXDaysValue = showLastXDays ? SHOW_LAST_X_DAYS_VALUES[showLastXDays] : null;
  const showLastXDaysUnit = showLastXDays ? SHOW_LAST_X_DAYS_UNIT[showLastXDays] : null;

  return {
    displayOptions: {
      chartType,
      plotStyle: ChartPlotStyle.Standard,
      analysis: AnalysisType.Linear,
      value: ValueRepresentationType.Absolute,
      ...(chartType === ChartType.FunnelSteps ? {funnelStepsSelectedTableColumns: DEFAULT_STEPS_TABLE_COLUMNS} : {}),
      // NOTE: This is used to differentiate bookmarks after we removed the
      // default softMin: 0 for Insights.
      primaryYAxisOptions: {},
    },
    sections: {
      globalDataGroupId: dataGroupId,
      metricLevelDataGroups,
      show: showEntry ? [showEntry] : [],
      time: [
        {
          ...(dataset && {dataset}),
          ...(showLastXDaysValue
            ? {
                dateRangeType: DATE_RANGE_TYPE.RELATIVE,
                value: null,
                window: {
                  value: showLastXDaysValue,
                  unit: TIME_UNITS.DAY,
                },
                unit: showLastXDaysUnit || TIME_UNITS.DAY,
              }
            : RANGE_TO_TIME_CLAUSE_PARAMS[RANGES.MONTH]),
        },
      ],
    },
    legend: {},
    sorting: _getDefaultInsightsSortConfig({numSegmentColumns: 1}),
    columnWidths: {},
    timeComparison: null,
  };
}

export function getAllEventsDayLimit(projectId: string): number | undefined {
  // We're extending the All Events override to all projects that have the insights metrics limit overridden
  const hasAllEventsLimitOverride = Object.keys(INSIGHTS_METRICS_LIMIT_OVERRIDES).includes(projectId);
  return hasAllEventsLimitOverride ? undefined : ALL_EVENTS_MAX_DAY_DURATION;
}
